import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import {
  cloneElement,
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  createElement,
  CSSProperties,
  ElementType,
  forwardRef,
  MouseEventHandler,
  PropsWithChildren,
  ReactElement,
  ReactHTML,
  useCallback
} from 'react';
import { useThemeColorPalette } from '../../hooks/use-theme-color-palette';
import { ThemeColors } from '../../theme';
import { Stack } from '../stack';
import { Typography, TypographyProps } from '../typography';
import classes from './button.module.scss';

export type ButtonProps = PropsWithChildren & {
  /**
   * Custom color definition
   */
  color?: ThemeColors;

  /**
   * Button variant
   */
  variant?: 'filled' | 'outlined' | 'text' | 'link' | 'light-filled';

  /**
   * Custom click handler
   */
  onClick?: MouseEventHandler<HTMLButtonElement>;

  /**
   * Custom button prefix placeholder for icons
   */
  prefix?: ReactElement;

  /**
   * Custom button suffix placeholder for icons
   */
  suffix?: ReactElement;

  /**
   * Render as disabled
   */
  disabled?: boolean;

  /**
   * Custom classname
   */
  className?: string;

  /**
   * Render loading indicator
   */
  loading?: boolean;

  /**
   * Represents the position where a loading indicator will appear.
   */
  loadingPosition?: 'prefix' | 'suffix';

  /**
   * Render as full width button
   */
  fullWidth?: boolean;

  /**
   * Render as max-content width button
   */
  maxWidth?: boolean;

  /**
   * Render as inline
   */
  inline?: boolean;

  /**
   * Specifies how to capitalize label
   */
  textTransform?: 'capitalize' | 'uppercase' | undefined;

  /**
   * This variable represents the optional `TypographyProps` for styling typo properties.
   */
  typoProps?: TypographyProps;

  /**
   * Represents the optional size of an element.
   *
   */
  size?: 'small' | 'medium';

  /**
   * Defines the horizontal alignment of content.
   *
   * The `align` property can be used to specify the alignment of the content within a given context.
   * It accepts one of the following string values:
   *
   * - `'center'`: Aligns the content to the center.
   * - `'left'`: Aligns the content to the left.
   *
   * This property is optional and, if not specified, may default to a specific value depending on the implementation.
   */
  align?: 'center' | 'left';

  /**
   * This variable represents the optional `classes` object for styling specific parts of the button component.
   */
  classes?: {
    label?: string;
  };

  label?: string;
};


type PolymorphicProps<T extends ElementType> = ButtonProps & {
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, keyof ButtonProps | 'as'>;

type PolymorphicRef<T extends ElementType> =
  ComponentPropsWithRef<T>['ref'];

type ButtonComponent = <T extends ElementType = 'button'>(
  props: PolymorphicProps<T> & {
    ref?: PolymorphicRef<T>;
  }
) => ReactElement | null;

/**
 * Simple button component
 */
export const ButtonImpl = <T extends ElementType = 'button'>({
  color = 'primary',
  prefix,
  suffix,
  disabled,
  variant = 'filled',
  className,
  children,
  onClick,
  loading = false,
  loadingPosition = 'prefix',
  fullWidth,
  maxWidth,
  inline,
  typoProps,
  textTransform,
  size = 'medium',
  align = 'center',
  classes: customClasses,
  as,
  ...props
}: PolymorphicProps<T>, ref: PolymorphicRef<T>) => {
  const palette = useThemeColorPalette(color ?? null);
  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    e => {
      if (loading || disabled) {
        return;
      }

      onClick?.(e);
    },
    [onClick, loading, disabled]
  );

  return createElement(
    as as keyof ReactHTML ?? 'button',
    {
      ...props,
      ref,
      role: props.role,
      onClick: onClick ? handleClick : undefined,
      className: clsx(
        classes.root,
        'button-component',
        `button-component--${variant}`,
        className,
        classes[color],
        classes[variant],
        {
          [classes.disabled]: disabled,
          [classes.aTag]: as !== 'button',
          [classes.loading]: loading,
          [classes.hasPrefix]: !!prefix || (loading && loadingPosition === 'prefix'),
          [classes.hasSuffix]: !!suffix || (loading && loadingPosition === 'suffix'),
          [classes.inline]: inline,
          [classes.fullWidth]: fullWidth,
          [classes.maxWidth]: maxWidth,
          [classes.small]: size === 'small',
          [classes.alignCenter]: align === 'center',
          [classes.alignLeft]: align === 'left',
        }
      ),
      disabled: disabled || loading,
      style: {
        ...props.style,
        '--button--color-main': palette?.main,
        '--button--color-dark': palette?.dark,
        '--button--color-light': palette?.light,
        '--button--color-contrast': palette?.contrast,
        '--button--color-ultra-light': palette?.['ultra-light'],
      } as CSSProperties,
    },
    <Stack as={'span'} className={classes.contentWrapper} fill crossAxis="center">
      <Stack as={'span'} className={classes.content} fill crossAxis="center">
        {(loading && loadingPosition === 'prefix') && (
          <FontAwesomeIcon className={classes.prefix} icon={['fas', 'spinner']} spin />
        )}

        {!(loading && loadingPosition === 'prefix') && prefix && cloneElement(prefix, { className: classes.prefix })}
        <span className={classes.labelWrapper}>
          <Typography
            variant="button"
            textTransform={textTransform}
            {...typoProps}
            className={clsx(classes.label, customClasses?.label, typoProps?.className)}
            noWrap
          >
            {children}
          </Typography>
        </span>

        {(loading && loadingPosition === 'suffix') && (
          <FontAwesomeIcon className={classes.prefix} icon={['fas', 'spinner']} spin />
        )}

        {!(loading && loadingPosition === 'suffix') && suffix && cloneElement(suffix, { className: classes.suffix })}
      </Stack>
    </Stack>
  );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const Button = forwardRef(ButtonImpl) as ButtonComponent;